import React, { useState } from 'react';
import { Modal, Descriptions, Tag, Button, Space, Input, Select, message, Divider } from 'antd';
import {
  UserOutlined, MailOutlined, PhoneOutlined, HomeOutlined,
  CalendarOutlined, ClockCircleOutlined, FileTextOutlined,
  EnvironmentOutlined, VideoCameraOutlined, 
  CheckCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import { format } from 'date-fns';

const { TextArea } = Input;
const { Option } = Select;

const InvestorRequestDetailModal = ({ request, isOpen, onClose, onStatusUpdate }) => {
  const [status, setStatus] = useState(request?.status || 'pending');
  const [notes, setNotes] = useState(request?.notes || '');
  const [updating, setUpdating] = useState(false);

  if (!request) return null;

  const getRequestTypeIcon = (type) => {
    switch (type) {
      case 'site-visit':
        return <EnvironmentOutlined style={{ fontSize: 24, color: '#470172' }} />;
      case 'land-allotment':
        return <FileTextOutlined style={{ fontSize: 24, color: '#470172' }} />;
      case 'virtual-meeting':
        return <VideoCameraOutlined style={{ fontSize: 24, color: '#470172' }} />;
      default:
        return null;
    }
  };

  const getRequestTypeLabel = (type) => {
    switch (type) {
      case 'site-visit':
        return 'Site Visit Request';
      case 'land-allotment':
        return 'Land Allotment Application';
      case 'virtual-meeting':
        return 'Virtual Meeting Request';
      default:
        return type;
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      'pending': { color: '#fa8c16', bg: '#fff7e6', border: '#ffd591', label: 'Pending' },
      'approved': { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', label: 'Approved' },
      'rejected': { color: '#f5222d', bg: '#fff1f0', border: '#ffa39e', label: 'Rejected' },
      'completed': { color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff', label: 'Completed' },
    };
    return configs[status] || configs['pending'];
  };

  const handleUpdateStatus = async () => {
    setUpdating(true);
    try {
      await onStatusUpdate(request._id, status, notes);
    } catch (error) {
      message.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const statusConfig = getStatusConfig(status);

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {getRequestTypeIcon(request.requestType)}
          <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '20px', fontWeight: 500, color: '#470172' }}>
            {getRequestTypeLabel(request.requestType)}
          </span>
        </div>
      }
      width={800}
      footer={[
        <Button 
          key="close" 
          onClick={onClose}
          style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400 }}
        >
          Close
        </Button>,
        <Button
          key="update"
          type="primary"
          loading={updating}
          onClick={handleUpdateStatus}
          disabled={status === request.status && notes === (request.notes || '')}
          style={{
            fontFamily: 'Rubik, sans-serif',
            fontSize: '16px',
            color: '#ffffff',
            fontWeight: 400,
            backgroundColor: '#470172',
            borderColor: '#470172'
          }}
        >
          Update Status
        </Button>,
      ]}
      style={{ top: 20 }}
    >
      <div style={{ fontFamily: 'Rubik, sans-serif' }}>
        {/* Status Update Section */}
        <div style={{ marginBottom: 24, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <label style={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px', fontWeight: 500, color: '#470172', display: 'block', marginBottom: 8 }}>
                Update Status
              </label>
              <Select
                value={status}
                onChange={setStatus}
                style={{ width: '100%', fontFamily: 'Rubik, sans-serif' }}
                size="large"
              >
                <Option value="pending">Pending</Option>
                <Option value="approved">Approved</Option>
                <Option value="rejected">Rejected</Option>
                <Option value="completed">Completed</Option>
              </Select>
            </div>
            <div>
              <label style={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px', fontWeight: 500, color: '#470172', display: 'block', marginBottom: 8 }}>
                Admin Notes
              </label>
              <TextArea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes about this request..."
                rows={3}
                style={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px' }}
              />
            </div>
          </Space>
        </div>

        <Divider />

        {/* Request Details */}
        <Descriptions
          column={1}
          labelStyle={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px', fontWeight: 500, color: '#470172' }}
          contentStyle={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px', color: '#595959' }}
        >
          <Descriptions.Item label={<><UserOutlined style={{ marginRight: 8 }} />Name</>}>
            {request.fullName || request.authorizedName || '—'}
          </Descriptions.Item>

          {request.companyName && (
            <Descriptions.Item label={<><HomeOutlined style={{ marginRight: 8 }} />Company Name</>}>
              {request.companyName}
            </Descriptions.Item>
          )}

          {request.company && (
            <Descriptions.Item label={<><HomeOutlined style={{ marginRight: 8 }} />Company</>}>
              {request.company}
            </Descriptions.Item>
          )}

          <Descriptions.Item label={<><MailOutlined style={{ marginRight: 8 }} />Email</>}>
            <a href={`mailto:${request.email}`} style={{ color: '#470172' }}>{request.email}</a>
          </Descriptions.Item>

          <Descriptions.Item label={<><PhoneOutlined style={{ marginRight: 8 }} />Phone</>}>
            <a href={`tel:${request.phone}`} style={{ color: '#470172' }}>{request.phone}</a>
          </Descriptions.Item>

          {request.sector && (
            <Descriptions.Item label="Sector of Interest">
              <Tag color="blue">{request.sector}</Tag>
            </Descriptions.Item>
          )}

          {request.industrySector && (
            <Descriptions.Item label="Industry Sector">
              <Tag color="blue">{request.industrySector}</Tag>
            </Descriptions.Item>
          )}

          {request.preferredDate && (
            <Descriptions.Item label={<><CalendarOutlined style={{ marginRight: 8 }} />Preferred Date</>}>
              {format(new Date(request.preferredDate), 'MMMM d, yyyy')}
            </Descriptions.Item>
          )}

          {request.preferredTime && (
            <Descriptions.Item label={<><ClockCircleOutlined style={{ marginRight: 8 }} />Preferred Time</>}>
              {request.preferredTime}
            </Descriptions.Item>
          )}

          {request.additionalRequirements && (
            <Descriptions.Item label="Additional Requirements">
              <div style={{ whiteSpace: 'pre-wrap' }}>{request.additionalRequirements}</div>
            </Descriptions.Item>
          )}

          {request.projectDetails && (
            <Descriptions.Item label="Project Details">
              <div style={{ whiteSpace: 'pre-wrap' }}>{request.projectDetails}</div>
            </Descriptions.Item>
          )}

          {request.topics && (
            <Descriptions.Item label="Discussion Topics">
              <div style={{ whiteSpace: 'pre-wrap' }}>{request.topics}</div>
            </Descriptions.Item>
          )}

          <Descriptions.Item label="Current Status">
            <Tag
              style={{
                fontSize: 13,
                fontFamily: "'Rubik', sans-serif",
                fontWeight: 600,
                border: `1px solid ${statusConfig.border}`,
                background: statusConfig.bg,
                color: statusConfig.color,
                padding: '4px 14px',
                borderRadius: 6,
              }}
            >
              {statusConfig.label}
            </Tag>
          </Descriptions.Item>

          <Descriptions.Item label="Submitted On">
            {request.createdAt ? format(new Date(request.createdAt), 'MMMM d, yyyy h:mm a') : '—'}
          </Descriptions.Item>

          {request.updatedAt && request.updatedAt !== request.createdAt && (
            <Descriptions.Item label="Last Updated">
              {format(new Date(request.updatedAt), 'MMMM d, yyyy h:mm a')}
            </Descriptions.Item>
          )}
        </Descriptions>
      </div>
    </Modal>
  );
};

export default InvestorRequestDetailModal;
